import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  ControlSafeAuditLog,
  ControlSafeAttachment,
  ControlSafeAccessLevel,
  ControlSafeField,
  ControlSafeFolder,
  ControlSafeItem,
  ControlSafeItemDetail,
  ControlSafeService,
  ControlSafeOverviewStats,
  ControlSafeVault
} from '../../services/controlsafe.service';
import { ControlSafeCryptoService } from '../../services/controlsafe-crypto.service';
import { UserService } from '../../services/user.service';
import { UserSessionService } from '../../services/user-session.service';

type ItemType = 'LOGIN' | 'SECURE_NOTE' | 'CREDIT_CARD' | 'DOCUMENT' | 'API_KEY' | 'WIFI' | 'IDENTITY' | 'BANK_ACCOUNT';
type VaultFolderRow = ControlSafeFolder & { depth: number };
type ShareRecipient = { email: string; access_level: ControlSafeAccessLevel };
type ItemSortColumn = 'title' | 'item_type' | 'username';
type DashboardMetric = {
  label: string;
  value: string;
  hint: string;
  icon: string;
  tone: 'blue' | 'green' | 'purple' | 'orange' | 'teal';
};

@Component({
  selector: 'app-controlsafe',
  templateUrl: './controlsafe.component.html',
  styleUrls: ['./controlsafe.component.scss']
})
export class ControlsafeComponent implements OnInit, OnDestroy {
  tenantId: number | null = null;
  userId: number | null = null;

  vaults: ControlSafeVault[] = [];
  folders: ControlSafeFolder[] = [];
  items: ControlSafeItem[] = [];
  currentVault: ControlSafeVault | null = null;
  currentFolder: ControlSafeFolder | null = null;
  breadcrumb: ControlSafeFolder[] = [];
  searchTerm = '';
  currentItemPage = 1;
  readonly itemPageSize = 10;
  itemSortColumn: ItemSortColumn = 'title';
  itemSortDirection: 'asc' | 'desc' = 'asc';
  folderIndex = new Map<number, ControlSafeFolder>();
  sidebarSection: 'overview' | 'folders' | 'items' | 'shared' | 'expiring' = 'overview';

  showVaultModal = false;
  showFolderModal = false;
  showItemModal = false;
  showItemDetail = false;
  itemSecretVisible = false;
  editingVault: ControlSafeVault | null = null;
  editingFolder: ControlSafeFolder | null = null;
  expandedVaultId: number | null = null;
  activeItemAccessLevel: ControlSafeAccessLevel = 'VIEW';
  activeItemCanEdit = false;
  activeItemCanShare = false;
  activeItemCanManage = false;
  activeItemCanDelete = false;

  vaultForm: FormGroup;
  folderForm: FormGroup;
  itemForm: FormGroup;
  detailForm: FormGroup;
  fieldForm: FormGroup;
  shareForm: FormGroup;
  uploading = false;
  savingVault = false;
  savingFolder = false;
  savingItem = false;
  vaultFeedback = '';
  folderFeedback = '';
  itemFeedback = '';
  shareModalVisible = false;
  shareSaving = false;
  shareLoading = false;
  shareFeedback = '';
  shareTarget: { type: 'VAULT' | 'FOLDER' | 'ITEM'; id: number; name: string } | null = null;
  shareRecipients: ShareRecipient[] = [];
  shareCanManage = false;
  shareSearchTerm = '';
  currentUserEmail = '';
  tenantUsersQuery = '';
  tenantUsersRequestId = 0;
  private shareUsersSearchTimeoutId: number | null = null;
  tenantUsers: Array<{ id: number; name: string; email: string; active: number }> = [];
  usersLoading = false;
  deleteTarget: { type: 'VAULT' | 'FOLDER' | 'ITEM'; id: number; name: string } | null = null;
  deleting = false;
  vaultFolderRowsByVaultId: Record<number, VaultFolderRow[]> = {};
  vaultFoldersLoading: Record<number, boolean> = {};
  dashboardStats: ControlSafeOverviewStats = {
    protected_items: 0,
    folders: 0,
    shared: 0,
    expiring_soon: 0,
    security_score: 0
  };

  cryptoKey: CryptoKey | null = null;
  private cryptoKeyUserId: number | null = null;
  private activeItemCryptoKey: CryptoKey | null = null;
  private activeItemCryptoOwnerId: number | null = null;

  activeItem: ControlSafeItemDetail | null = null;
  activeItemSecret = '';
  itemFields: ControlSafeField[] = [];
  attachments: ControlSafeAttachment[] = [];
  itemHistory: ControlSafeAuditLog[] = [];
  itemHistoryPage = 1;
  itemHistoryPageSize = 5;
  itemHistoryTotal = 0;
  itemHistoryLoading = false;
  detailEditMode = false;
  detailSaving = false;
  detailFeedback = '';
  fieldDisplayValues = new Map<number, string>();
  showSecret = false;
  detailTab: 'details' | 'shares' | 'history' | 'attachments' = 'details';
  activeVaultMenuId: number | null = null;
  activeFolderMenuId: number | null = null;
  activeItemMenuId: number | null = null;
  activeItemMenuPosition: { top: number; left: number } | null = null;
  showQuickCreateMenu = false;
  itemFiles: File[] = [];
  summaryExpanded = true;
  vaultsExpanded = true;
  banks: Array<{ code?: string; name: string }> = [];
  banksLoading = false;
  banksError = '';
  copyFeedback = '';
  private subscriptions: Array<{ unsubscribe: () => void }> = [];
  private copyTimeoutId?: number;

  itemTypes: Array<{ value: ItemType; label: string }> = [
    { value: 'LOGIN', label: 'Login' },
    { value: 'SECURE_NOTE', label: 'Nota segura' },
    { value: 'CREDIT_CARD', label: 'Cartão' },
    { value: 'DOCUMENT', label: 'Documento' },
    { value: 'API_KEY', label: 'API Key' },
    { value: 'WIFI', label: 'Wi-Fi' },
    { value: 'IDENTITY', label: 'Identidade' },
    { value: 'BANK_ACCOUNT', label: 'Conta bancária' }
  ];

  fieldTypes = [
    { value: 'TEXT', label: 'Texto' },
    { value: 'NUMBER', label: 'Número' },
    { value: 'URL', label: 'URL' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'DATE', label: 'Data' },
    { value: 'PASSWORD', label: 'Senha' },
    { value: 'SECRET', label: 'Segredo' }
  ];

  constructor(
    private fb: FormBuilder,
    private controlsafe: ControlSafeService,
    private cryptoService: ControlSafeCryptoService,
    private userService: UserService,
    private session: UserSessionService
  ) {
    this.vaultForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: ['']
    });
    this.folderForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
    this.itemForm = this.fb.group({
      vault_id: ['', Validators.required],
      item_type: ['LOGIN', Validators.required],
      title: ['', [Validators.required, Validators.minLength(2)]],
      subtitle: [''],
      website_url: [''],
      username: [''],
      secret: [''],
      notes_preview: [''],
      expires_at: [null],
      is_favorite: [false],
      card_type: [''],
      card_bank: [''],
      card_number: [''],
      card_expiration: [''],
      api_key_prod: [''],
      api_key_hml: [''],
      bank_account_type: [''],
      bank_id_number: [''],
      bank_account_number: [''],
      bank_swift: [''],
      bank_iban: [''],
      bank_pin: [''],
      bank_country: [''],
      bank_branch_address: [''],
      bank_phone: [''],
      bank_branch: [''],
      bank_manager: ['']
    });
    this.detailForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(2)]],
      subtitle: [''],
      website_url: [''],
      username: [''],
      secret: [''],
      notes_preview: [''],
      expires_at: [''],
      is_favorite: [false]
    });
    this.fieldForm = this.fb.group({
      field_label: ['', [Validators.required, Validators.minLength(2)]],
      field_key: ['', [Validators.required, Validators.minLength(2)]],
      field_type: ['TEXT', Validators.required],
      value: ['']
    });
    this.shareForm = this.fb.group({
      emails: ['']
    });
  }

  ngOnInit() {
    const user = this.session.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    this.currentUserEmail = String((user as any)?.email || '').trim().toLowerCase();
    if (this.tenantId && this.userId) {
      void this.ensureCryptoKey();
      this.loadVaults();
      this.loadBanks();
      this.loadItems();
      this.loadOverviewStats();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
    if (this.copyTimeoutId) {
      window.clearTimeout(this.copyTimeoutId);
    }
    this.clearShareUsersSearchTimer();
  }

  get filteredFolders() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.folders;
    }
    return this.folders.filter((folder) => folder.name.toLowerCase().includes(term));
  }

  get filteredVaults() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.vaults;
    }
    return this.vaults.filter((vault) =>
      [vault.name, vault.description].some((value) => String(value || '').toLowerCase().includes(term))
    );
  }

  get folderListRows() {
    if (!this.currentVault) {
      return [];
    }
    const rows = this.vaultFolderRowsByVaultId[this.currentVault.id] || [];
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((folder) => folder.name.toLowerCase().includes(term));
  }

  get favoriteItems() {
    return (this.items || []).filter((item) => !!item.is_favorite && this.isOwnedByCurrentUser(item.created_by));
  }

  get favoriteVaults() {
    return this.filteredVaults.filter((vault) => !!vault.is_favorite && this.isOwnedByCurrentUser(vault.created_by));
  }

  get favoriteFolders() {
    return this.folderListRows.filter((folder) => !!folder.is_favorite && this.isOwnedByCurrentUser(folder.created_by));
  }

  get favoriteContainersLabel() {
    return this.currentVault ? 'Pastas favoritas' : 'Cofres favoritos';
  }

  trackByVaultId(_index: number, vault: ControlSafeVault) {
    return vault.id;
  }

  trackByFolderId(_index: number, folder: ControlSafeFolder) {
    return folder.id;
  }

  trackByFavoriteRowId(_index: number, row: { id: number }) {
    return row.id;
  }

  isOwnedByCurrentUser(createdBy: number | null | undefined) {
    return !!this.userId && Number(createdBy || 0) === Number(this.userId || 0);
  }

  canFavoriteVault(vault: ControlSafeVault) {
    return this.isOwnedByCurrentUser(vault.created_by);
  }

  canFavoriteFolder(folder: ControlSafeFolder) {
    return this.isOwnedByCurrentUser(folder.created_by);
  }

  canFavoriteItem(item: ControlSafeItem) {
    return this.isOwnedByCurrentUser(item.created_by);
  }

  private shouldIgnoreRowClick(event?: Event) {
    const target = event?.target instanceof HTMLElement ? event.target : null;
    return !!target?.closest('button, a, input, select, textarea, mat-select');
  }

  canDeleteVault(vault: ControlSafeVault) {
    return this.isOwnedByCurrentUser(vault.created_by);
  }

  canDeleteFolder(folder: ControlSafeFolder) {
    return this.isOwnedByCurrentUser(folder.created_by);
  }

  canDeleteActiveItem() {
    return this.activeItemAccessLevel === 'MANAGE' && this.activeItemCanDelete;
  }

  canEditActiveItem() {
    return this.isOwnedByCurrentUser(this.activeItem?.created_by) && this.activeItemCanEdit;
  }

  canShareActiveItem() {
    return this.isOwnedByCurrentUser(this.activeItem?.created_by) && this.activeItemCanShare;
  }

  canShowItemHistory() {
    return this.activeItemAccessLevel === 'MANAGE' && this.activeItemCanManage;
  }

  canShowItemAttachments() {
    return this.activeItemAccessLevel === 'MANAGE' && this.activeItemCanManage;
  }

  get filteredItems() {
    let items = this.items;
    if (this.sidebarSection === 'shared') {
      items = items.filter((item) => !!item.shared_access);
    } else if (this.sidebarSection === 'expiring') {
      const now = new Date();
      const limit = new Date();
      limit.setDate(now.getDate() + 30);
      items = items.filter((item) => {
        if (!item.expires_at) {
          return false;
        }
        const expires = new Date(item.expires_at);
        return !Number.isNaN(expires.getTime()) && expires >= now && expires <= limit;
      });
    }
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((item) =>
      [item.title, item.subtitle, item.username, item.website_url].some((value) =>
        String(value || '').toLowerCase().includes(term)
      )
    );
  }

  get sortedFilteredItems() {
    const direction = this.itemSortDirection === 'asc' ? 1 : -1;
    const valueFor = (item: ControlSafeItem) => {
      if (this.itemSortColumn === 'item_type') {
        return this.getItemTypeLabel(item.item_type);
      }
      if (this.itemSortColumn === 'username') {
        return String(item.username || '');
      }
      return String(item.title || '');
    };
    return [...this.filteredItems].sort((left, right) => {
      const a = valueFor(left).toLocaleLowerCase();
      const b = valueFor(right).toLocaleLowerCase();
      return a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }) * direction;
    });
  }

  get paginatedItems() {
    const start = (this.currentItemPage - 1) * this.itemPageSize;
    return this.sortedFilteredItems.slice(start, start + this.itemPageSize);
  }

  get itemTotalPages() {
    return Math.max(1, Math.ceil(this.sortedFilteredItems.length / this.itemPageSize));
  }

  get itemPageNumbers() {
    return Array.from({ length: this.itemTotalPages }, (_, index) => index + 1);
  }

  get itemPageFrom() {
    if (!this.sortedFilteredItems.length) {
      return 0;
    }
    return (this.currentItemPage - 1) * this.itemPageSize + 1;
  }

  get itemPageTo() {
    return Math.min(this.sortedFilteredItems.length, this.currentItemPage * this.itemPageSize);
  }

  get filteredShareUsers() {
    const selected = new Set(this.shareRecipients.map((recipient) => String(recipient?.email || '').trim().toLowerCase()));
    return this.tenantUsers
      .filter((user) => Number(user?.active || 0) === 1)
      .filter((user) => {
        const email = String(user?.email || '').toLowerCase();
        if (!email || selected.has(email)) {
          return false;
        }
        return true;
      })
      .slice(0, 20);
  }

  private async deriveCryptoKeyFor(userId: number | null | undefined) {
    if (!this.tenantId || !userId) {
      return null;
    }
    const password = `controlsafe:${this.tenantId}:${userId}`;
    const salt = 'controlsafe-auto';
    return this.cryptoService.deriveKey(password, salt);
  }

  private async ensureCryptoKey() {
    if (this.cryptoKey && this.cryptoKeyUserId === this.userId) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.cryptoKey = await this.deriveCryptoKeyFor(this.userId);
    this.cryptoKeyUserId = this.userId;
  }

  private async ensureActiveItemCryptoKey(ownerUserId?: number | null) {
    const normalizedOwnerId = Number(ownerUserId || 0) || this.userId || null;
    if (this.activeItemCryptoKey && this.activeItemCryptoOwnerId === normalizedOwnerId) {
      return;
    }
    if (!this.tenantId || !normalizedOwnerId) {
      return;
    }
    this.activeItemCryptoKey = await this.deriveCryptoKeyFor(normalizedOwnerId);
    this.activeItemCryptoOwnerId = normalizedOwnerId;
  }

  loadVaults() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlsafe.listVaults(this.tenantId, this.userId).subscribe((vaults) => {
      this.vaults = vaults;
      this.loadOverviewStats();
    });
  }

  get shareSelectedEmails() {
    return this.shareRecipients.map((recipient) => recipient.email);
  }

  private normalizeShareRecipients(
    recipients: Array<{ email: string; access_level?: ControlSafeAccessLevel | null }> | string[]
  ): ShareRecipient[] {
    const map = new Map<string, ControlSafeAccessLevel>();
    for (const recipient of recipients || []) {
      if (typeof recipient === 'string') {
        const email = String(recipient || '').trim().toLowerCase();
        if (!email || !email.includes('@')) {
          continue;
        }
        map.set(email, map.get(email) || 'VIEW');
        continue;
      }
      const email = String(recipient?.email || '').trim().toLowerCase();
      if (!email || !email.includes('@')) {
        continue;
      }
      map.set(email, (recipient?.access_level || map.get(email) || 'VIEW') as ControlSafeAccessLevel);
    }
    return Array.from(map.entries()).map(([email, access_level]) => ({ email, access_level }));
  }

  private upsertShareRecipient(email: string, access_level: ControlSafeAccessLevel = 'VIEW') {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) {
      return;
    }
    const existing = this.shareRecipients.find((recipient) => recipient.email === normalized);
    if (existing) {
      existing.access_level = access_level;
      this.shareRecipients = [...this.shareRecipients];
      return;
    }
    this.shareRecipients = [...this.shareRecipients, { email: normalized, access_level }];
  }

  removeShareRecipient(email: string) {
    const normalized = String(email || '').trim().toLowerCase();
    if (this.isCurrentUserShareRecipient(normalized)) {
      return;
    }
    this.shareRecipients = this.shareRecipients.filter((recipient) => recipient.email !== normalized);
  }

  updateShareRecipientAccessLevel(email: string, accessLevel: ControlSafeAccessLevel) {
    const normalized = String(email || '').trim().toLowerCase();
    if (this.isCurrentUserShareRecipient(normalized)) {
      return;
    }
    this.shareRecipients = this.shareRecipients.map((recipient) =>
      recipient.email === normalized ? { ...recipient, access_level: accessLevel } : recipient
    );
  }

  toggleVaultFavorite(vault: ControlSafeVault, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.tenantId || !this.userId || !this.canFavoriteVault(vault)) {
      return;
    }
    const previous = vault.is_favorite ? 1 : 0;
    const next = previous ? 0 : 1;
    vault.is_favorite = next;
    this.controlsafe.updateVaultFavorite(vault.id, this.tenantId, this.userId, next === 1).subscribe({
      error: () => {
        vault.is_favorite = previous;
      }
    });
  }

  toggleFolderFavorite(folder: ControlSafeFolder, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.tenantId || !this.userId || !this.canFavoriteFolder(folder)) {
      return;
    }
    const previous = folder.is_favorite ? 1 : 0;
    const next = previous ? 0 : 1;
    folder.is_favorite = next;
    this.controlsafe.updateFolderFavorite(folder.id, this.tenantId, this.userId, next === 1).subscribe({
      error: () => {
        folder.is_favorite = previous;
      }
    });
  }

  loadBanks() {
    this.banksLoading = true;
    this.banksError = '';
    this.controlsafe.listBanks().subscribe({
      next: (banks) => {
        this.banks = banks || [];
        this.banksLoading = false;
      },
      error: () => {
        this.banksLoading = false;
        this.banksError = 'Não foi possível carregar a lista de bancos.';
      }
    });
  }

  openVault(vault: ControlSafeVault) {
    this.currentVault = vault;
    this.currentFolder = null;
    this.breadcrumb = [];
    this.currentItemPage = 1;
    this.sidebarSection = 'overview';
    this.loadFolders();
    this.loadItems();
    void this.ensureVaultFolderTree(vault);
  }

  openVaultFromRow(vault: ControlSafeVault, event?: Event) {
    if (this.shouldIgnoreRowClick(event)) {
      return;
    }
    this.openVault(vault);
  }

  goToRoot() {
    this.currentFolder = null;
    this.breadcrumb = [];
    this.currentVault = null;
    this.currentItemPage = 1;
    this.sidebarSection = 'overview';
    this.loadFolders();
    this.loadItems();
  }

  openFolder(folder: ControlSafeFolder) {
    this.currentFolder = folder;
    this.breadcrumb = this.buildBreadcrumb(folder);
    this.activeFolderMenuId = null;
    this.currentItemPage = 1;
    this.sidebarSection = 'items';
    this.loadFolders();
    this.loadItems();
  }

  openFolderFromRow(folder: ControlSafeFolder, event?: Event) {
    if (this.shouldIgnoreRowClick(event)) {
      return;
    }
    this.openFolder(folder);
  }

  openItemFromRow(item: ControlSafeItem, event?: Event) {
    if (this.shouldIgnoreRowClick(event)) {
      return;
    }
    this.openItemDetail(item);
  }

  navigateTo(folder: ControlSafeFolder, index: number) {
    this.currentFolder = folder;
    this.breadcrumb = this.breadcrumb.slice(0, index + 1);
    this.activeFolderMenuId = null;
    this.currentItemPage = 1;
    this.sidebarSection = 'items';
    this.loadFolders();
    this.loadItems();
  }

  loadFolders() {
    if (!this.tenantId || !this.userId || !this.currentVault) {
      this.folders = [];
      return;
    }
    this.controlsafe
      .listFolders(this.tenantId, this.userId, this.currentVault.id, this.currentFolder?.id ?? null)
      .subscribe((folders) => {
        this.folders = folders;
        this.rememberFolders(folders);
      });
  }

  loadItems() {
    if (!this.tenantId || !this.userId) {
      this.items = [];
      return;
    }
    if (this.sidebarSection === 'shared') {
      this.controlsafe.listAccessibleItems(this.tenantId, this.userId).subscribe((items) => {
        this.items = this.mergePasswordScores(items.filter((item) => !!item.shared_access));
        this.currentItemPage = Math.min(this.currentItemPage, Math.max(1, Math.ceil(this.items.length / this.itemPageSize)));
        this.loadOverviewStats();
      });
      return;
    }
    if (!this.currentVault) {
      this.controlsafe.listAccessibleItems(this.tenantId, this.userId).subscribe((items) => {
        this.items = this.mergePasswordScores(items);
        this.currentItemPage = Math.min(this.currentItemPage, Math.max(1, Math.ceil(this.items.length / this.itemPageSize)));
        this.loadOverviewStats();
      });
      return;
    }
    this.controlsafe
      .listItems(this.tenantId, this.userId, this.currentVault.id, this.currentFolder?.id ?? null)
      .subscribe((items) => {
        this.items = this.mergePasswordScores(items);
        this.currentItemPage = Math.min(this.currentItemPage, Math.max(1, Math.ceil(this.items.length / this.itemPageSize)));
        this.rememberFolders(this.folders);
        this.loadOverviewStats();
      });
  }

  onItemSearchChange(value: string) {
    this.searchTerm = value || '';
    this.currentItemPage = 1;
  }

  setSidebarSection(section: 'overview' | 'folders' | 'items' | 'shared' | 'expiring', event?: Event) {
    event?.stopPropagation();
    this.sidebarSection = section;
    this.currentItemPage = 1;
    if (section === 'shared') {
      this.currentVault = null;
      this.currentFolder = null;
      this.breadcrumb = [];
      this.loadFolders();
      this.loadItems();
      return;
    }
    if (section === 'folders') {
      this.currentFolder = null;
      this.breadcrumb = [];
      this.currentVault = null;
      this.activeFolderMenuId = null;
      this.loadVaults();
      return;
    }
    if (section === 'overview' || section === 'items' || section === 'expiring') {
      if (section === 'overview' && this.currentVault) {
        this.currentFolder = null;
        this.breadcrumb = [];
      }
      this.loadFolders();
      this.loadItems();
    }
  }

  goToItemPage(page: number) {
    const totalPages = this.itemTotalPages;
    const nextPage = Math.min(Math.max(1, page), totalPages);
    this.currentItemPage = nextPage;
  }

  setItemSort(column: ItemSortColumn) {
    if (this.itemSortColumn === column) {
      this.itemSortDirection = this.itemSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.itemSortColumn = column;
      this.itemSortDirection = 'asc';
    }
    this.currentItemPage = 1;
  }

  isItemSortActive(column: ItemSortColumn) {
    return this.itemSortColumn === column;
  }

  loadOverviewStats() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const sub = this.controlsafe.getOverviewStats(this.tenantId, this.userId).subscribe({
      next: (stats) => {
        this.dashboardStats = {
          protected_items: Number(stats?.protected_items || 0),
          folders: Number(stats?.folders || 0),
          shared: Number(stats?.shared || 0),
          expiring_soon: Number(stats?.expiring_soon || 0),
          security_score: Number(stats?.security_score || 0)
        };
      },
      error: () => {
        this.dashboardStats = {
          protected_items: this.items.length,
          folders: this.currentVault ? this.folders.length : this.vaults.length,
          shared: this.sharedItemsCount,
          expiring_soon: this.expiringSoonCount,
          security_score: this.securityScore
        };
      }
    });
    this.subscriptions.push(sub);
  }

  openVaultModal(vault?: ControlSafeVault) {
    this.editingVault = vault || null;
    this.vaultFeedback = '';
    this.savingVault = false;
    this.activeVaultMenuId = null;
    this.vaultForm.reset({
      name: vault?.name || '',
      description: vault?.description || ''
    });
    this.showVaultModal = true;
  }

  saveVault() {
    if (!this.vaultForm.valid || !this.tenantId || !this.userId) {
      this.vaultForm.markAllAsTouched();
      this.vaultFeedback = 'Preencha os campos obrigatórios.';
      return;
    }
    this.vaultFeedback = '';
    this.savingVault = true;
    const payload = {
      tenant_id: this.tenantId,
      name: this.vaultForm.value.name,
      description: this.vaultForm.value.description || null,
      created_by: this.userId
    };
    if (this.editingVault) {
      this.controlsafe
        .updateVault(this.editingVault.id, {
          tenant_id: this.tenantId,
          user_id: this.userId,
          name: payload.name,
          description: payload.description
        })
        .subscribe({
          next: () => {
            this.savingVault = false;
            this.showVaultModal = false;
            this.loadVaults();
          },
          error: () => {
            this.savingVault = false;
            this.vaultFeedback = 'Não foi possível salvar o cofre.';
          }
        });
      return;
    }
    this.controlsafe.createVault(payload).subscribe({
      next: () => {
        this.savingVault = false;
        this.showVaultModal = false;
        this.loadVaults();
      },
      error: () => {
        this.savingVault = false;
        this.vaultFeedback = 'Não foi possível salvar o cofre.';
      }
    });
  }

  deleteVault(vault: ControlSafeVault, event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = null;
    this.activeFolderMenuId = null;
    this.activeItemMenuId = null;
    this.deleteTarget = { type: 'VAULT', id: vault.id, name: vault.name };
  }

  toggleVaultMenu(vault: ControlSafeVault, event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = this.activeVaultMenuId === vault.id ? null : vault.id;
  }

  closeVaultMenu(event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = null;
  }

  toggleFolderMenu(folder: ControlSafeFolder, event?: Event) {
    event?.stopPropagation();
    this.activeFolderMenuId = this.activeFolderMenuId === folder.id ? null : folder.id;
  }

  closeFolderMenu(event?: Event) {
    event?.stopPropagation();
    this.activeFolderMenuId = null;
  }

  toggleItemMenu(item: ControlSafeItem, event?: Event) {
    event?.stopPropagation();
    if (this.activeItemMenuId === item.id) {
      this.activeItemMenuId = null;
      this.activeItemMenuPosition = null;
      return;
    }
    const target = event?.currentTarget as HTMLElement | null;
    const rect = target?.getBoundingClientRect();
    this.activeItemMenuId = item.id;
    this.activeItemMenuPosition = rect
      ? {
          top: Math.round(rect.bottom + 8),
          left: Math.round(rect.right - 168)
        }
      : null;
  }

  closeItemMenu(event?: Event) {
    event?.stopPropagation();
    this.activeItemMenuId = null;
    this.activeItemMenuPosition = null;
  }

  @HostListener('document:click')
  closeActionMenus() {
    this.activeVaultMenuId = null;
    this.activeFolderMenuId = null;
    this.activeItemMenuId = null;
    this.activeItemMenuPosition = null;
    this.showQuickCreateMenu = false;
  }

  toggleQuickCreateMenu(event?: Event) {
    event?.stopPropagation();
    this.showQuickCreateMenu = !this.showQuickCreateMenu;
  }

  closeQuickCreateMenu(event?: Event) {
    event?.stopPropagation();
    this.showQuickCreateMenu = false;
  }

  openShareModal(type: 'VAULT' | 'FOLDER' | 'ITEM', target: { id: number; name: string }, event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = null;
    this.activeFolderMenuId = null;
    this.activeItemMenuId = null;
    this.activeItemMenuPosition = null;
    this.shareTarget = { type, id: target.id, name: target.name };
    this.shareLoading = true;
    this.shareSaving = false;
    this.shareFeedback = '';
    this.shareSearchTerm = '';
    this.shareRecipients = [];
    this.shareCanManage = false;
    this.shareModalVisible = true;

    if (!this.tenantId || !this.userId) {
      this.shareLoading = false;
      return;
    }

    this.ensureTenantUsersLoaded('');
    const sub = this.controlsafe.getShareGrants(this.tenantId, this.userId, type, target.id).subscribe({
      next: (response) => {
        this.shareRecipients = this.normalizeShareRecipients(response?.recipients || response?.emails || []);
        this.shareCanManage = !!response?.can_manage && (this.activeItem ? this.isOwnedByCurrentUser(this.activeItem.created_by) : true);
        this.shareLoading = false;
      },
      error: () => {
        this.shareLoading = false;
        this.shareFeedback = 'Não foi possível carregar os usuários compartilhados.';
      }
    });
    this.subscriptions.push(sub);
  }

  closeShareModal() {
    this.clearShareUsersSearchTimer();
    this.shareModalVisible = false;
    this.shareLoading = false;
    this.shareSaving = false;
    this.shareFeedback = '';
    this.shareTarget = null;
    this.shareRecipients = [];
    this.shareCanManage = false;
    this.shareSearchTerm = '';
  }

  onShareSearchChange(value: string) {
    this.shareSearchTerm = value || '';
    this.clearShareUsersSearchTimer();
    this.shareUsersSearchTimeoutId = window.setTimeout(() => {
      this.ensureTenantUsersLoaded(this.shareSearchTerm);
    }, 250);
  }

  saveShareGrants() {
    if (!this.tenantId || !this.userId || !this.shareTarget || this.shareSaving || !this.shareCanManage) {
      return;
    }
    const recipients = this.normalizeShareRecipients(this.shareRecipients);
    this.shareSaving = true;
    this.shareFeedback = '';
    const sub = this.controlsafe
      .saveShareGrants({
        tenant_id: this.tenantId,
        user_id: this.userId,
        target_type: this.shareTarget.type,
        target_id: this.shareTarget.id,
        recipients
      })
      .subscribe({
        next: (response) => {
          this.shareSaving = false;
          this.shareRecipients = this.normalizeShareRecipients(response?.recipients || response?.emails || []);
          this.shareFeedback = 'Compartilhamento atualizado.';
          this.loadVaults();
          if (this.currentVault) {
            this.loadFolders();
            this.loadItems();
          }
          if (this.shareTarget?.type === 'ITEM' && this.activeItem?.id === this.shareTarget.id) {
            this.loadItemHistory(this.activeItem.id, 1);
          }
        },
        error: (error) => {
          this.shareSaving = false;
          this.shareFeedback = error?.error?.error || 'Não foi possível salvar o compartilhamento.';
        }
      });
    this.subscriptions.push(sub);
  }

  toggleShareUser(email: string, checked: boolean) {
    if (!this.shareCanManage) {
      return;
    }
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) {
      return;
    }
    if (checked) {
      this.upsertShareRecipient(normalized, 'VIEW');
      return;
    }
    this.removeShareRecipient(normalized);
  }

  isCurrentUserShareRecipient(email: string) {
    const normalized = String(email || '').trim().toLowerCase();
    return !!normalized && !!this.currentUserEmail && normalized === this.currentUserEmail;
  }

  private normalizeDisplayText(value: string | null | undefined): string {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  private normalizeSearchText(value: string | null | undefined): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private clearShareUsersSearchTimer() {
    if (this.shareUsersSearchTimeoutId) {
      window.clearTimeout(this.shareUsersSearchTimeoutId);
      this.shareUsersSearchTimeoutId = null;
    }
  }

  private ensureTenantUsersLoaded(searchTerm = '') {
    if (!this.tenantId) {
      return;
    }
    const normalizedSearch = this.normalizeSearchText(searchTerm);
    if (this.tenantUsers.length && normalizedSearch === this.tenantUsersQuery && !this.usersLoading) {
      return;
    }
    const requestId = ++this.tenantUsersRequestId;
    this.usersLoading = true;
    const sub = this.userService.getUsersByTenant(this.tenantId, normalizedSearch).subscribe({
      next: (rows) => {
        if (requestId !== this.tenantUsersRequestId) {
          return;
        }
        this.usersLoading = false;
        this.tenantUsersQuery = normalizedSearch;
        this.tenantUsers = (rows || [])
          .map((row: any) => ({
            id: Number(row?.id || 0),
            name: this.normalizeDisplayText(row?.name || ''),
            email: String(row?.email || '').toLowerCase(),
            active: Number(row?.active || 0)
          }))
          .filter((row) => !!row.id && !!row.email);
      },
      error: () => {
        if (requestId !== this.tenantUsersRequestId) {
          return;
        }
        this.usersLoading = false;
        this.tenantUsers = [];
        this.tenantUsersQuery = normalizedSearch;
      }
    });
    this.subscriptions.push(sub);
  }

  toggleSummaryPanel(event?: Event) {
    event?.stopPropagation();
    this.summaryExpanded = !this.summaryExpanded;
  }

  toggleVaultsPanel(event?: Event) {
    event?.stopPropagation();
    this.vaultsExpanded = !this.vaultsExpanded;
  }

  openFolderModal(folder?: ControlSafeFolder) {
    this.editingFolder = folder || null;
    this.folderForm.reset({ name: folder?.name || '' });
    this.folderFeedback = '';
    this.savingFolder = false;
    this.showFolderModal = true;
  }

  saveFolder() {
    if (!this.folderForm.valid || !this.tenantId || !this.userId || !this.currentVault) {
      this.folderForm.markAllAsTouched();
      this.folderFeedback = 'Preencha os campos obrigatórios.';
      return;
    }
    this.folderFeedback = '';
    this.savingFolder = true;
    const payload = {
      tenant_id: this.tenantId,
      vault_id: this.currentVault.id,
      parent_folder_id: this.currentFolder?.id || null,
      name: this.folderForm.value.name,
      created_by: this.userId
    };
    const request$ = this.editingFolder
      ? this.controlsafe.updateFolder(this.editingFolder.id, {
          tenant_id: this.tenantId,
          user_id: this.userId,
          name: this.folderForm.value.name
        })
      : this.controlsafe.createFolder(payload);
    request$.subscribe({
      next: () => {
        this.savingFolder = false;
        this.showFolderModal = false;
        this.editingFolder = null;
        this.loadFolders();
      },
      error: () => {
        this.savingFolder = false;
        this.folderFeedback = 'Não foi possível salvar a pasta.';
      }
    });
  }

  deleteFolder(folder: ControlSafeFolder, event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = null;
    this.activeFolderMenuId = null;
    this.activeItemMenuId = null;
    this.deleteTarget = { type: 'FOLDER', id: folder.id, name: folder.name };
  }

  openItemModal(initialType: ItemType = 'LOGIN') {
    this.itemForm.reset({
      vault_id: this.currentVault?.id || '',
      item_type: initialType,
      title: '',
      subtitle: '',
      website_url: '',
      username: '',
      secret: '',
      notes_preview: '',
      expires_at: null,
      is_favorite: false,
      card_type: '',
      card_bank: '',
      card_number: '',
      card_expiration: '',
      api_key_prod: '',
      api_key_hml: '',
      bank_account_type: '',
      bank_id_number: '',
      bank_account_number: '',
      bank_swift: '',
      bank_iban: '',
      bank_pin: '',
      bank_country: '',
      bank_branch_address: '',
      bank_phone: '',
      bank_branch: '',
      bank_manager: ''
    });
    this.itemFeedback = '';
    this.savingItem = false;
    this.itemFiles = [];
    this.itemSecretVisible = false;
    this.showItemModal = true;
  }

  async saveItem() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.itemForm.markAllAsTouched();
    if (!this.validateItemFormForType()) {
      return;
    }
    this.itemFeedback = '';
    this.savingItem = true;
    await this.ensureCryptoKey();
    if (!this.cryptoKey) {
      this.savingItem = false;
      this.itemFeedback = 'Não foi possível validar a criptografia.';
      return;
    }
    const itemType = this.itemForm.value.item_type as ItemType;
    const selectedVaultId = Number(this.itemForm.value.vault_id || this.currentVault?.id || 0);
    if (!selectedVaultId) {
      this.savingItem = false;
      this.itemFeedback = 'Selecione o cofre para este item.';
      return;
    }
    const secret = this.resolveSecretForType(itemType);
    const encrypted = await this.cryptoService.encrypt(secret, this.cryptoKey);
    const notesPreview = this.itemForm.value.notes_preview || null;
    const payload = {
      tenant_id: this.tenantId,
      vault_id: selectedVaultId,
      folder_id: this.currentFolder && this.currentVault && Number(this.currentVault.id) === selectedVaultId ? this.currentFolder.id : null,
      item_type: itemType,
      title: this.resolveTitleForType(itemType),
      subtitle: this.itemForm.value.subtitle || null,
      website_url: itemType === 'LOGIN' ? this.itemForm.value.website_url || null : null,
      username: this.itemForm.value.username || null,
      secret_ciphertext: encrypted.ciphertext,
      secret_iv: encrypted.iv,
      secret_tag: encrypted.tag,
      crypto_alg: 'AES-256-GCM',
      key_version: 1,
      notes_preview: notesPreview,
      password_score: this.getPasswordStrengthScore(secret),
      expires_at: this.usesExpiresAt(itemType) ? this.formatItemDateForSave(this.itemForm.value.expires_at) : null,
      is_favorite: this.itemForm.value.is_favorite,
      created_by: this.userId
    };
    this.controlsafe.createItem(payload).subscribe({
      next: async (created) => {
        try {
          const extraPayloads = await this.buildExtraFieldPayloads(itemType);
          for (const fieldPayload of extraPayloads) {
            await firstValueFrom(this.controlsafe.createField(created.id, fieldPayload));
          }
          for (const file of this.itemFiles) {
            await firstValueFrom(
              this.controlsafe.uploadAttachment(created.id, {
                tenant_id: this.tenantId!,
                user_id: this.userId!,
                file
              })
            );
          }
          this.savingItem = false;
          this.showItemModal = false;
          this.itemFiles = [];
          this.loadItems();
        } catch {
          this.savingItem = false;
          this.itemFeedback = 'Item criado, mas houve erro ao salvar detalhes.';
          this.loadItems();
        }
      },
      error: () => {
        this.savingItem = false;
        this.itemFeedback = 'Não foi possível salvar o item.';
      }
    });
  }

  async openItemDetail(item: ControlSafeItem) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.activeItemCryptoKey = null;
    this.activeItemCryptoOwnerId = null;
    this.controlsafe.getItem(item.id, this.tenantId, this.userId).subscribe(async (detail) => {
      this.activeItem = detail;
      this.detailTab = 'details';
      this.detailEditMode = false;
      this.detailFeedback = '';
      this.showSecret = false;
      this.shareCanManage = !!detail.can_manage && this.isOwnedByCurrentUser(detail.created_by);
      await this.ensureActiveItemCryptoKey(detail.created_by);
      if (this.activeItemCryptoKey) {
        try {
          this.activeItemSecret = await this.cryptoService.decrypt(
            detail.secret_ciphertext,
            detail.secret_iv,
            detail.secret_tag,
            this.activeItemCryptoKey
          );
          const passwordScore = this.getPasswordStrengthScore(this.activeItemSecret);
          this.activeItem = {
            ...detail,
            password_score: passwordScore
          };
          this.items = this.items.map((entry) =>
            Number(entry.id || 0) === Number(detail.id || 0)
              ? { ...entry, password_score: passwordScore }
              : entry
          );
        } catch {
          this.activeItemSecret = 'Falha ao descriptografar.';
          this.activeItem = detail;
        }
      } else {
        this.activeItemSecret = '';
        this.activeItem = detail;
      }
      this.populateDetailForm(detail);
      this.loadItemShareGrants(detail.id);
      this.loadItemFields(detail.id);
      if (detail.can_manage) {
        this.loadAttachments(detail.id);
        this.loadItemHistory(detail.id, 1);
      } else {
        this.attachments = [];
        this.itemHistory = [];
        this.itemHistoryTotal = 0;
      }
      this.showItemDetail = true;
    });
  }

  closeItemDetail() {
    this.showItemDetail = false;
    this.activeItem = null;
    this.activeItemSecret = '';
    this.activeItemCryptoKey = null;
    this.activeItemCryptoOwnerId = null;
    this.itemFields = [];
    this.attachments = [];
    this.itemHistory = [];
    this.itemHistoryPage = 1;
    this.itemHistoryTotal = 0;
    this.itemHistoryLoading = false;
    this.fieldDisplayValues.clear();
    this.showSecret = false;
    this.detailEditMode = false;
    this.detailSaving = false;
    this.detailFeedback = '';
    this.detailForm.reset({
      title: '',
      subtitle: '',
      website_url: '',
      username: '',
      secret: '',
      notes_preview: '',
      expires_at: '',
      is_favorite: false
    });
    this.activeItemAccessLevel = 'VIEW';
    this.activeItemCanEdit = false;
    this.activeItemCanShare = false;
    this.activeItemCanManage = false;
    this.activeItemCanDelete = false;
    if (this.shareTarget?.type === 'ITEM') {
      this.shareTarget = null;
      this.shareRecipients = [];
      this.shareSearchTerm = '';
      this.shareFeedback = '';
      this.shareLoading = false;
      this.shareCanManage = false;
    }
    this.clearShareUsersSearchTimer();
  }

  deleteItem(item: ControlSafeItem, event?: Event) {
    event?.stopPropagation();
    this.activeVaultMenuId = null;
    this.activeFolderMenuId = null;
    this.activeItemMenuId = null;
    this.activeItemMenuPosition = null;
    this.deleteTarget = { type: 'ITEM', id: item.id, name: item.title };
  }

  cancelDeleteTarget() {
    this.deleteTarget = null;
    this.deleting = false;
  }

  confirmDeleteTarget() {
    if (!this.deleteTarget || !this.tenantId || !this.userId || this.deleting) {
      return;
    }
    this.deleting = true;
    const target = this.deleteTarget;
    if (target.type === 'VAULT') {
      this.controlsafe.deleteVault(target.id, this.tenantId, this.userId).subscribe({
        next: () => {
          if (this.currentVault?.id === target.id) {
            this.currentVault = null;
            this.items = [];
            this.folders = [];
          }
          this.loadVaults();
          this.cancelDeleteTarget();
        },
        error: () => {
          this.deleting = false;
        }
      });
      return;
    }
    if (target.type === 'FOLDER') {
      this.controlsafe.deleteFolder(target.id, this.tenantId, this.userId).subscribe({
        next: () => {
          this.loadFolders();
          this.loadItems();
          this.cancelDeleteTarget();
        },
        error: () => {
          this.deleting = false;
        }
      });
      return;
    }
    this.controlsafe.deleteItem(target.id, this.tenantId, this.userId).subscribe({
      next: () => {
        this.loadItems();
        this.cancelDeleteTarget();
      },
      error: () => {
        this.deleting = false;
      }
    });
  }

  async loadItemFields(itemId: number) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlsafe.listFields(itemId, this.tenantId, this.userId).subscribe((fields) => {
      this.itemFields = fields;
      this.fieldDisplayValues.clear();
      void this.ensureActiveItemCryptoKey(this.activeItem?.created_by).then(() => {
        if (!this.activeItemCryptoKey) {
          return;
        }
        Promise.all(
          fields.map(async (field) => {
            if (field.value_plain) {
              this.fieldDisplayValues.set(field.id, field.value_plain);
              return;
            }
            if (field.value_ciphertext && field.value_iv && field.value_tag) {
              try {
                const decrypted = await this.cryptoService.decrypt(
                  field.value_ciphertext,
                  field.value_iv,
                  field.value_tag,
                  this.activeItemCryptoKey!
                );
                this.fieldDisplayValues.set(field.id, decrypted);
              } catch {
                this.fieldDisplayValues.set(field.id, '***');
              }
            }
          })
        );
      });
    });
  }

  loadItemShareGrants(itemId: number) {
    if (!this.tenantId || !this.userId || !this.activeItem) {
      return;
    }
    this.shareTarget = { type: 'ITEM', id: itemId, name: this.activeItem.title };
    this.shareLoading = true;
    this.shareFeedback = '';
    const sub = this.controlsafe.getShareGrants(this.tenantId, this.userId, 'ITEM', itemId).subscribe({
      next: (response) => {
        this.shareRecipients = this.normalizeShareRecipients(response?.recipients || response?.emails || []);
        this.shareCanManage = !!response?.can_manage;
        this.shareLoading = false;
      },
      error: () => {
        this.shareLoading = false;
        this.shareFeedback = 'Não foi possível carregar os compartilhamentos do item.';
      }
    });
    this.subscriptions.push(sub);
  }

  loadItemHistory(itemId: number, page = 1) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.itemHistoryPage = page;
    this.itemHistoryLoading = true;
    this.detailFeedback = '';
    const sub = this.controlsafe.listItemHistory(itemId, this.tenantId, this.userId, page, this.itemHistoryPageSize).subscribe({
      next: (response) => {
        this.itemHistory = response?.entries || [];
        this.itemHistoryPage = Number(response?.page || page);
        this.itemHistoryPageSize = Number(response?.page_size || this.itemHistoryPageSize);
        this.itemHistoryTotal = Number(response?.total || 0);
        this.itemHistoryLoading = false;
      },
      error: () => {
        this.itemHistory = [];
        this.itemHistoryTotal = 0;
        this.itemHistoryLoading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  private populateDetailForm(detail: ControlSafeItemDetail) {
    this.detailForm.reset({
      title: detail.title || '',
      subtitle: detail.subtitle || '',
      website_url: detail.website_url || '',
      username: detail.username || '',
      secret: this.activeItemSecret || '',
      notes_preview: detail.notes_preview || '',
      expires_at: detail.expires_at ? String(detail.expires_at).slice(0, 10) : '',
      is_favorite: !!detail.is_favorite
    });
    this.activeItemAccessLevel = (detail.access_level || 'VIEW') as ControlSafeAccessLevel;
    this.activeItemCanEdit = !!detail.can_edit;
    this.activeItemCanShare = !!detail.can_share;
    this.activeItemCanManage = !!detail.can_manage;
    this.activeItemCanDelete = !!detail.can_delete;
    this.detailForm.markAsPristine();
  }

  async addField() {
    if (!this.activeItem || !this.tenantId || !this.userId) {
      return;
    }
    await this.ensureActiveItemCryptoKey(this.activeItem.created_by);
    if (!this.activeItemCryptoKey) {
      return;
    }
    if (!this.fieldForm.valid) {
      this.fieldForm.markAllAsTouched();
      return;
    }
    const fieldType = this.fieldForm.value.field_type;
    const value = String(this.fieldForm.value.value || '');
    let payload: any = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      field_key: this.fieldForm.value.field_key,
      field_label: this.fieldForm.value.field_label,
      field_type: fieldType
    };
    if (fieldType === 'PASSWORD' || fieldType === 'SECRET') {
      const encrypted = await this.cryptoService.encrypt(value, this.activeItemCryptoKey);
      payload = {
        ...payload,
        value_ciphertext: encrypted.ciphertext,
        value_iv: encrypted.iv,
        value_tag: encrypted.tag
      };
    } else {
      payload = { ...payload, value_plain: value };
    }
    this.controlsafe.createField(this.activeItem.id, payload).subscribe(() => {
      this.fieldForm.reset({ field_label: '', field_key: '', field_type: 'TEXT', value: '' });
      this.loadItemFields(this.activeItem!.id);
    });
  }

  loadAttachments(itemId: number) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlsafe.listAttachments(itemId, this.tenantId, this.userId).subscribe((attachments) => {
      this.attachments = attachments;
    });
  }

  async uploadAttachment(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file || !this.activeItem || !this.tenantId || !this.userId) {
      return;
    }
    this.uploading = true;
    this.controlsafe.uploadAttachment(this.activeItem.id, {
      tenant_id: this.tenantId,
      user_id: this.userId,
      file
    }).subscribe({
      next: () => {
        this.uploading = false;
        if (input) {
          input.value = '';
        }
        this.loadAttachments(this.activeItem!.id);
      },
      error: () => {
        this.uploading = false;
      }
    });
  }

  downloadAttachment(attachment: ControlSafeAttachment) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlsafe.downloadAttachment(attachment.id, this.tenantId, this.userId).subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  }

  copySecret() {
    if (!this.activeItemSecret) {
      return;
    }
    navigator.clipboard?.writeText(this.activeItemSecret).then(() => {
      this.showCopyFeedback('Senha copiada.');
    });
  }

  copyToClipboard(value?: string | null) {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return;
    }
    navigator.clipboard?.writeText(normalized).then(() => {
      this.showCopyFeedback('Texto copiado.');
    });
  }

  getWebsiteHref(value?: string | null) {
    const url = String(value || '').trim();
    if (!url) {
      return '';
    }
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(url)) {
      return url;
    }
    return `https://${url}`;
  }

  toggleSecretVisibility() {
    this.showSecret = !this.showSecret;
  }

  setDetailTab(tab: 'details' | 'shares' | 'history' | 'attachments', event?: Event) {
    event?.stopPropagation();
    if (tab === 'history' && !this.canShowItemHistory()) {
      return;
    }
    if (tab === 'attachments' && !this.canShowItemAttachments()) {
      return;
    }
    this.detailTab = tab;
    if (tab === 'shares' && this.activeItem) {
      this.ensureTenantUsersLoaded(this.shareSearchTerm);
      this.loadItemShareGrants(this.activeItem.id);
    }
    if (tab === 'history' && this.activeItem) {
      this.loadItemHistory(this.activeItem.id, this.itemHistoryPage || 1);
    }
  }

  startDetailEdit(event?: Event) {
    event?.stopPropagation();
    if (!this.activeItem || !this.activeItemCanEdit) {
      return;
    }
    this.detailEditMode = true;
    this.detailFeedback = '';
    this.populateDetailForm(this.activeItem);
  }

  cancelDetailEdit(event?: Event) {
    event?.stopPropagation();
    if (!this.activeItem) {
      return;
    }
    this.detailEditMode = false;
    this.detailFeedback = '';
    this.populateDetailForm(this.activeItem);
  }

  async saveDetailItem() {
    if (!this.activeItem || !this.tenantId || !this.userId || this.detailSaving || !this.activeItemCanEdit) {
      return;
    }
    this.detailForm.markAllAsTouched();
    if (!this.detailForm.value.title || !String(this.detailForm.value.title || '').trim()) {
      this.detailFeedback = 'Informe o título do item.';
      return;
    }
    this.detailSaving = true;
    this.detailFeedback = '';
    await this.ensureActiveItemCryptoKey(this.activeItem.created_by);
    if (!this.activeItemCryptoKey) {
      this.detailSaving = false;
      this.detailFeedback = 'Não foi possível validar a criptografia.';
      return;
    }
    const secretValue = String(this.detailForm.value.secret || this.activeItemSecret || '');
    const encrypted = await this.cryptoService.encrypt(secretValue, this.activeItemCryptoKey);
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      title: String(this.detailForm.value.title || '').trim(),
      subtitle: this.detailForm.value.subtitle || null,
      website_url: this.detailForm.value.website_url || null,
      username: this.detailForm.value.username || null,
      secret_ciphertext: encrypted.ciphertext,
      secret_iv: encrypted.iv,
      secret_tag: encrypted.tag,
      notes_preview: this.detailForm.value.notes_preview || null,
      password_score: this.getPasswordStrengthScore(secretValue),
      expires_at: this.detailForm.value.expires_at || null,
      is_favorite: !!this.detailForm.value.is_favorite,
      status: this.activeItem.status
    };
    const sub = this.controlsafe.updateItem(this.activeItem.id, payload).subscribe({
      next: () => {
        this.detailSaving = false;
        this.detailEditMode = false;
        this.activeItemSecret = secretValue;
        const passwordScore = this.getPasswordStrengthScore(secretValue);
        this.activeItem = {
          ...this.activeItem!,
          title: payload.title,
          subtitle: payload.subtitle,
          website_url: payload.website_url,
          username: payload.username,
          notes_preview: payload.notes_preview,
          expires_at: payload.expires_at,
          is_favorite: payload.is_favorite ? 1 : 0,
          password_score: passwordScore,
          updated_at: new Date().toISOString()
        };
        this.items = this.items.map((entry) =>
          Number(entry.id || 0) === Number(this.activeItem?.id || 0)
            ? {
                ...entry,
                title: payload.title,
                subtitle: payload.subtitle,
                website_url: payload.website_url,
                username: payload.username,
                notes_preview: payload.notes_preview,
                expires_at: payload.expires_at,
                is_favorite: payload.is_favorite ? 1 : 0,
                password_score: passwordScore
              }
            : entry
        );
        this.populateDetailForm(this.activeItem);
        this.loadItems();
        this.loadOverviewStats();
        this.loadItemHistory(this.activeItem.id, 1);
      },
      error: () => {
        this.detailSaving = false;
        this.detailFeedback = 'Não foi possível salvar as alterações.';
      }
    });
    this.subscriptions.push(sub);
  }

  goToItemHistoryPage(page: number) {
    if (!this.activeItem) {
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.itemHistoryTotal / this.itemHistoryPageSize));
    const nextPage = Math.min(Math.max(1, page), totalPages);
    if (nextPage === this.itemHistoryPage) {
      return;
    }
    this.loadItemHistory(this.activeItem.id, nextPage);
  }

  async toggleVaultFolders(vault: ControlSafeVault, event?: Event) {
    event?.stopPropagation();
    this.expandedVaultId = this.expandedVaultId === vault.id ? null : vault.id;
    this.activeVaultMenuId = null;
    if (this.expandedVaultId === vault.id) {
      await this.ensureVaultFolderTree(vault);
    }
  }

  toggleVaultDescription(vault: ControlSafeVault, event?: Event) {
    return this.toggleVaultFolders(vault, event);
  }

  isVaultFoldersExpanded(vaultId: number) {
    return this.expandedVaultId === vaultId;
  }

  getVaultFolderRows(vaultId: number) {
    return this.vaultFolderRowsByVaultId[vaultId] || [];
  }

  openFolderFromSidebar(vault: ControlSafeVault, folder: ControlSafeFolder, event?: Event) {
    event?.stopPropagation();
    this.currentVault = vault;
    this.currentFolder = folder;
    this.breadcrumb = this.buildBreadcrumb(folder);
    this.sidebarSection = 'items';
    this.currentItemPage = 1;
    this.loadFolders();
    this.loadItems();
  }

  private async ensureVaultFolderTree(vault: ControlSafeVault) {
    if (!this.tenantId || !this.userId || this.vaultFoldersLoading[vault.id] || this.vaultFolderRowsByVaultId[vault.id]) {
      return;
    }
    this.vaultFoldersLoading[vault.id] = true;
    const rows: VaultFolderRow[] = [];
    try {
      await this.loadVaultFolderBranch(vault.id, null, 0, rows);
      this.vaultFolderRowsByVaultId = {
        ...this.vaultFolderRowsByVaultId,
        [vault.id]: rows
      };
    } finally {
      this.vaultFoldersLoading[vault.id] = false;
    }
  }

  private async loadVaultFolderBranch(vaultId: number, parentId: number | null, depth: number, rows: VaultFolderRow[]) {
    if (!this.tenantId || !this.userId) {
      return;
    }
    const folders = await firstValueFrom(this.controlsafe.listFolders(this.tenantId, this.userId, vaultId, parentId));
    for (const folder of folders || []) {
      const current = folder as VaultFolderRow;
      this.folderIndex.set(current.id, current);
      rows.push({ ...current, depth });
      await this.loadVaultFolderBranch(vaultId, current.id, depth + 1, rows);
    }
  }

  private rememberFolders(folders: ControlSafeFolder[]) {
    folders.forEach((folder) => {
      this.folderIndex.set(folder.id, folder);
    });
  }

  private buildBreadcrumb(folder: ControlSafeFolder) {
    const chain: ControlSafeFolder[] = [];
    const visited = new Set<number>();
    let current: ControlSafeFolder | undefined = folder;
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      chain.unshift(current);
      const parentId: number | null = current.parent_folder_id ? Number(current.parent_folder_id) : null;
      current = parentId ? this.folderIndex.get(parentId) : undefined;
    }
    return chain;
  }

  toggleFavorite(item: ControlSafeItem, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.tenantId || !this.userId || !this.canFavoriteItem(item)) {
      return;
    }
    const previous = item.is_favorite ? 1 : 0;
    const next = previous ? 0 : 1;
    item.is_favorite = next;
    this.controlsafe.updateItemFavorite(item.id, this.tenantId, this.userId, next === 1).subscribe({
      error: () => {
        item.is_favorite = previous;
      }
    });
  }

  async copyItemSecret(item: ControlSafeItem, event?: Event) {
    event?.stopPropagation();
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.controlsafe.getItem(item.id, this.tenantId, this.userId).subscribe(async (detail) => {
      await this.ensureActiveItemCryptoKey(detail.created_by);
      if (!this.activeItemCryptoKey) {
        return;
      }
      try {
        const decrypted = await this.cryptoService.decrypt(
          detail.secret_ciphertext,
          detail.secret_iv,
          detail.secret_tag,
          this.activeItemCryptoKey
        );
        navigator.clipboard?.writeText(decrypted).then(() => {
          this.showCopyFeedback('Senha copiada.');
        });
      } catch {
        return;
      }
    });
  }

  private showCopyFeedback(message: string) {
    this.copyFeedback = message;
    if (this.copyTimeoutId) {
      window.clearTimeout(this.copyTimeoutId);
    }
    this.copyTimeoutId = window.setTimeout(() => {
      this.copyFeedback = '';
    }, 1800);
  }

  onNewItemFilesSelected(event: Event | DragEvent) {
    const dragFiles = event instanceof DragEvent ? Array.from(event.dataTransfer?.files || []) : [];
    const input = event.target as HTMLInputElement | null;
    const files = dragFiles.length ? dragFiles : Array.from(input?.files || []);
    if (!files.length) {
      return;
    }
    this.itemFiles = [...this.itemFiles, ...files];
    if (input) {
      input.value = '';
    }
  }

  removeNewItemFile(index: number) {
    this.itemFiles.splice(index, 1);
  }

  get selectedItemType(): ItemType {
    return this.itemForm.get('item_type')?.value as ItemType;
  }

  getItemTypeDisplayLabel(itemType: ItemType): string {
    return this.itemTypes.find((type) => type.value === itemType)?.label || 'Selecione';
  }

  get titleLabel(): string {
    switch (this.selectedItemType) {
      case 'CREDIT_CARD':
        return 'Nome do cartão';
      case 'BANK_ACCOUNT':
        return 'Nome do banco';
      default:
        return 'Título';
    }
  }

  get secretLabel(): string {
    if (this.selectedItemType === 'LOGIN' || this.selectedItemType === 'WIFI' || this.selectedItemType === 'CREDIT_CARD') {
      return 'Senha';
    }
    if (this.selectedItemType === 'BANK_ACCOUNT') {
      return 'PIN';
    }
    return 'Segredo';
  }

  get detailSecretLabel(): string {
    const type = this.activeItem?.item_type as ItemType | undefined;
    if (!type) {
      return 'Segredo';
    }
    if (type === 'LOGIN' || type === 'WIFI' || type === 'CREDIT_CARD') {
      return 'Senha';
    }
    if (type === 'BANK_ACCOUNT') {
      return 'PIN';
    }
    return 'Segredo';
  }

  get itemSecretStrengthScore(): number {
    return this.getPasswordStrengthScore(this.itemForm.value.secret);
  }

  get itemSecretStrengthLabel(): string {
    const score = this.itemSecretStrengthScore;
    if (score >= 4) {
      return 'Forte';
    }
    if (score >= 2) {
      return 'Média';
    }
    return 'Fraca';
  }

  get itemSecretStrengthTone(): 'weak' | 'medium' | 'strong' {
    const score = this.itemSecretStrengthScore;
    if (score >= 4) {
      return 'strong';
    }
    if (score >= 2) {
      return 'medium';
    }
    return 'weak';
  }

  toggleItemSecretVisibility() {
    this.itemSecretVisible = !this.itemSecretVisible;
  }

  generateItemPassword(length = 16) {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()-_=+[]{}';
    const source = globalThis.crypto;
    const values = new Uint32Array(length);
    if (source?.getRandomValues) {
      source.getRandomValues(values);
    } else {
      for (let index = 0; index < values.length; index += 1) {
        values[index] = Math.floor(Math.random() * 0xffffffff);
      }
    }
    const password = Array.from(values, (value) => charset[value % charset.length]).join('');
    this.itemForm.patchValue({ secret: password });
    this.itemSecretVisible = true;
  }

  openItemWebsite() {
    const url = String(this.itemForm.value.website_url || '').trim();
    if (!url) {
      return;
    }
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(normalized, '_blank', 'noopener,noreferrer');
  }

  get summaryStats() {
    return [
      {
        label: 'Itens',
        value: this.items.length.toString(),
        icon: 'inventory_2'
      },
      {
        label: 'Cofres',
        value: this.vaults.length.toString(),
        icon: 'folder'
      },
      {
        label: 'Compartilhados',
        value: this.sharedItemsCount.toString(),
        icon: 'share'
      },
      {
        label: 'Última atualização',
        value: this.lastActivityLabel,
        icon: 'schedule'
      },
      {
        label: 'Criado em',
        value: this.formatShortDate(this.currentVault?.created_at),
        icon: 'event'
      }
    ];
  }

  get mainSectionTitle() {
    if (this.sidebarSection === 'shared') {
      return 'Compartilhados';
    }
    if (this.sidebarSection === 'folders') {
      return 'Cofres';
    }
    if (this.sidebarSection === 'expiring') {
      return 'Credenciais expirando';
    }
    return 'Itens protegidos';
  }

  get mainSectionCount() {
    if (this.sidebarSection === 'folders') {
      return this.currentVault ? this.folderListRows.length : this.filteredVaults.length;
    }
    return this.filteredItems.length;
  }

  get mainSearchLabel() {
    if (this.sidebarSection === 'shared') {
      return 'Buscar compartilhados...';
    }
    return this.sidebarSection === 'folders'
      ? this.currentVault
        ? 'Buscar pastas...'
        : 'Buscar cofres...'
      : 'Buscar itens, logins, senhas, certificados...';
  }

  get dashboardMetricCards(): DashboardMetric[] {
    const stats = this.dashboardStats;
    return [
      {
        label: 'Itens protegidos',
        value: String(stats.protected_items ?? this.items.length),
        hint: 'Visão geral',
        icon: 'lock',
        tone: 'blue'
      },
      {
        label: 'Cofres',
        value: String(this.vaults.length),
        hint: 'Cofres disponíveis',
        icon: 'folder',
        tone: 'green'
      },
      {
        label: 'Compartilhados',
        value: String(stats.shared ?? this.sharedItemsCount),
        hint: 'Acesso concedido',
        icon: 'people',
        tone: 'purple'
      },
      {
        label: 'Expiram em breve',
        value: String(stats.expiring_soon ?? this.expiringSoonCount),
        hint: 'Próximos 30 dias',
        icon: 'schedule',
        tone: 'orange'
      },
      {
        label: 'Senhas fortes',
        value: `${this.passwordStrengthPercent}%`,
        hint: this.passwordStrengthLabel,
        icon: 'verified_user',
        tone: 'teal'
      }
    ];
  }

  vaultItemCount(vault: ControlSafeVault) {
    return Number(vault.item_count || 0);
  }

  vaultFolderCount(vault: ControlSafeVault) {
    return Number(vault.folder_count || 0);
  }

  getItemIcon(itemType: string): string {
    switch (itemType) {
      case 'DOCUMENT':
        return 'description';
      case 'CREDIT_CARD':
        return 'credit_card';
      case 'API_KEY':
        return 'vpn_key';
      case 'WIFI':
        return 'wifi';
      case 'IDENTITY':
        return 'badge';
      case 'BANK_ACCOUNT':
        return 'account_balance';
      case 'SECURE_NOTE':
        return 'sticky_note_2';
      default:
        return 'lock';
    }
  }

  getItemTypeLabel(itemType: string): string {
    switch (itemType) {
      case 'LOGIN':
        return 'LOGIN';
      case 'CREDIT_CARD':
        return 'CONTA BANCÁRIA';
      case 'DOCUMENT':
        return 'DOCUMENTO';
      case 'API_KEY':
        return 'API KEY';
      case 'WIFI':
        return 'WIFI';
      case 'IDENTITY':
        return 'IDENTIDADE';
      case 'BANK_ACCOUNT':
        return 'BANCO';
      case 'SECURE_NOTE':
        return 'NOTA';
      default:
        return itemType;
    }
  }

  usesExpiresAt(type: ItemType) {
    return type === 'LOGIN' || type === 'DOCUMENT' || type === 'API_KEY';
  }

  private resolveTitleForType(_type: ItemType) {
    const value = String(this.itemForm.value.title || '').trim();
    return value;
  }

  private resolveSecretForType(type: ItemType) {
    if (type === 'API_KEY') {
      return String(this.itemForm.value.api_key_prod || '');
    }
    if (type === 'SECURE_NOTE') {
      return String(this.itemForm.value.notes_preview || '');
    }
    if (type === 'BANK_ACCOUNT') {
      return String(this.itemForm.value.bank_pin || '');
    }
    return String(this.itemForm.value.secret || '');
  }

  private formatItemDateForSave(value: unknown) {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private validateItemFormForType() {
    const type = this.itemForm.value.item_type as ItemType;
    Object.values(this.itemForm.controls).forEach((control) => control.setErrors(null));
    const requiredFields: Array<{ field: string; label: string }> = [];
    requiredFields.push({ field: 'vault_id', label: 'Cofre' });
    switch (type) {
      case 'LOGIN':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'username', label: 'Usuário' },
          { field: 'secret', label: 'Senha' }
        );
        break;
      case 'SECURE_NOTE':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'notes_preview', label: 'Notas' }
        );
        break;
      case 'CREDIT_CARD':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'card_type', label: 'Tipo do cartão' },
          { field: 'card_bank', label: 'Banco' },
          { field: 'card_number', label: 'Número' },
          { field: 'card_expiration', label: 'Expiração do cartão' },
          { field: 'secret', label: 'Senha' }
        );
        break;
      case 'DOCUMENT':
        requiredFields.push({ field: 'title', label: 'Título' });
        break;
      case 'API_KEY':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'api_key_prod', label: 'API Key produção' }
        );
        break;
      case 'WIFI':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'username', label: 'Usuário' },
          { field: 'secret', label: 'Senha' }
        );
        break;
      case 'BANK_ACCOUNT':
        requiredFields.push(
          { field: 'title', label: 'Título' },
          { field: 'bank_account_type', label: 'Tipo de conta' },
          { field: 'bank_id_number', label: 'Número de identificação' },
          { field: 'bank_account_number', label: 'Número da conta' },
          { field: 'bank_swift', label: 'Código SWIFT' },
          { field: 'bank_iban', label: 'Número IBAN' },
          { field: 'bank_pin', label: 'PIN' },
          { field: 'bank_country', label: 'País' },
          { field: 'bank_branch_address', label: 'Endereço da filial' },
          { field: 'bank_phone', label: 'Telefone' },
          { field: 'bank_branch', label: 'Filial' },
          { field: 'bank_manager', label: 'Gerente da conta' }
        );
        break;
      default:
        requiredFields.push({ field: 'title', label: 'Título' });
        break;
    }
    let valid = true;
    const missingLabels: string[] = [];
    requiredFields.forEach(({ field, label }) => {
      const control = this.itemForm.get(field);
      if (!control || !String(control.value || '').trim()) {
        control?.setErrors({ required: true });
        missingLabels.push(label);
        valid = false;
      }
    });
    this.itemFeedback = valid ? '' : `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`;
    return valid;
  }

  private async buildExtraFieldPayloads(itemType: ItemType) {
    if (!this.tenantId || !this.userId || !this.cryptoKey) {
      return [];
    }
    const fields: Array<{ key: string; label: string; type: string; value: string }> = [];
    if (itemType === 'CREDIT_CARD') {
      const cardTypeValue = this.itemForm.value.card_type === 'CREDITO' ? 'Crédito' : this.itemForm.value.card_type === 'DEBITO' ? 'Débito' : this.itemForm.value.card_type;
      fields.push(
        { key: 'card_type', label: 'Tipo do cartão', type: 'TEXT', value: cardTypeValue },
        { key: 'card_bank', label: 'Banco', type: 'TEXT', value: this.itemForm.value.card_bank },
        { key: 'card_number', label: 'Número', type: 'TEXT', value: this.itemForm.value.card_number },
        { key: 'card_expiration', label: 'Expiração', type: 'TEXT', value: this.itemForm.value.card_expiration }
      );
    }
    if (itemType === 'API_KEY') {
      fields.push(
        { key: 'api_key_prod', label: 'API Key produção', type: 'SECRET', value: this.itemForm.value.api_key_prod },
        { key: 'api_key_hml', label: 'API Key homologação', type: 'SECRET', value: this.itemForm.value.api_key_hml }
      );
    }
    if (itemType === 'BANK_ACCOUNT') {
      fields.push(
        { key: 'bank_account_type', label: 'Tipo de conta', type: 'TEXT', value: this.itemForm.value.bank_account_type },
        { key: 'bank_id_number', label: 'Número de identificação', type: 'TEXT', value: this.itemForm.value.bank_id_number },
        { key: 'bank_account_number', label: 'Número da conta', type: 'TEXT', value: this.itemForm.value.bank_account_number },
        { key: 'bank_swift', label: 'Código SWIFT', type: 'TEXT', value: this.itemForm.value.bank_swift },
        { key: 'bank_iban', label: 'Número IBAN', type: 'TEXT', value: this.itemForm.value.bank_iban },
        { key: 'bank_country', label: 'País', type: 'TEXT', value: this.itemForm.value.bank_country },
        { key: 'bank_branch_address', label: 'Endereço da filial', type: 'TEXT', value: this.itemForm.value.bank_branch_address },
        { key: 'bank_phone', label: 'Telefone', type: 'TEXT', value: this.itemForm.value.bank_phone },
        { key: 'bank_branch', label: 'Filial', type: 'TEXT', value: this.itemForm.value.bank_branch },
        { key: 'bank_manager', label: 'Gerente da conta', type: 'TEXT', value: this.itemForm.value.bank_manager }
      );
    }
    const payloads = [];
    for (const field of fields) {
      const value = String(field.value || '').trim();
      if (!value) {
        continue;
      }
      let payload: any = {
        tenant_id: this.tenantId,
        user_id: this.userId,
        field_key: field.key,
        field_label: field.label,
        field_type: field.type
      };
      if (field.type === 'PASSWORD' || field.type === 'SECRET') {
        const encrypted = await this.cryptoService.encrypt(value, this.cryptoKey);
        payload = {
          ...payload,
          value_ciphertext: encrypted.ciphertext,
          value_iv: encrypted.iv,
          value_tag: encrypted.tag
        };
      } else {
        payload = { ...payload, value_plain: value };
      }
      payloads.push(payload);
    }
    return payloads;
  }

  get sharedItemsCount() {
    return new Set(this.items.filter((item) => !!item.shared_access).map((item) => Number(item.id || 0)).filter((id) => !!id)).size;
  }

  get expiringSoonCount() {
    const now = new Date();
    const limit = new Date();
    limit.setDate(now.getDate() + 30);
    return this.items.filter((item) => {
      if (!item.expires_at) {
        return false;
      }
      const expires = new Date(item.expires_at);
      return !Number.isNaN(expires.getTime()) && expires >= now && expires <= limit;
    }).length;
  }

  get securityScore() {
    return this.passwordStrengthPercent;
  }

  get passwordStrengthPercent() {
    const relevantItems = this.items.filter((item) => this.isPasswordRelevantItem(item));
    if (!relevantItems.length) {
      return 0;
    }
    const strongCount = relevantItems.filter((item) => Number(item.password_score || 0) >= 4).length;
    return Math.round((strongCount / relevantItems.length) * 100);
  }

  get passwordStrengthLabel() {
    const percent = this.passwordStrengthPercent;
    if (percent >= 90) {
      return 'Excelente';
    }
    if (percent >= 75) {
      return 'Bom';
    }
    return 'Atenção';
  }

  get passwordStrengthCopy() {
    const percent = this.passwordStrengthPercent;
    if (percent >= 90) {
      return 'Seu cofre está muito seguro';
    }
    if (percent >= 75) {
      return 'Boa parte das senhas está forte';
    }
    return 'Há senhas que precisam de revisão';
  }

  get lastActivityLabel() {
    const dates = [
      ...this.items.map((item) => item.updated_at || item.created_at || ''),
      ...this.folders.map((folder) => folder.updated_at || folder.created_at || ''),
      this.currentVault?.updated_at || ''
    ].filter(Boolean) as string[];
    if (!dates.length) {
      return '—';
    }
    const latest = dates
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0];
    return latest ? this.formatDateTime(latest.toISOString()) : '—';
  }

  private formatShortDate(value?: string | null) {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  private formatDateTime(value?: string | null) {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  get itemPath(): string {
    if (!this.currentVault) {
      return 'Raiz';
    }
    const folderPath = this.breadcrumb.length ? this.breadcrumb.map((folder) => folder.name).join(' / ') : '';
    return `Raiz / ${this.currentVault.name}${folderPath ? ` / ${folderPath}` : ''}`;
  }

  get currentPathLabel(): string {
    if (!this.currentVault) {
      return 'Raiz';
    }
    const folderPath = this.breadcrumb.length ? this.breadcrumb.map((folder) => folder.name).join(' / ') : '';
    return `Raiz / ${this.currentVault.name}${folderPath ? ` / ${folderPath}` : ''}`;
  }

  get detailPasswordStrengthLabel() {
    const score = this.detailPasswordStrengthScore;
    if (score >= 4) {
      return 'Forte';
    }
    if (score >= 3) {
      return 'Boa';
    }
    return 'Fraca';
  }

  get detailPasswordStrengthTone() {
    return this.detailPasswordStrengthScore >= 4 ? 'success' : 'warning';
  }

  get detailPasswordStrengthDots() {
    return Array.from({ length: 5 }, (_, index) => index + 1);
  }

  get detailPasswordStrengthScore() {
    const secretValue = this.detailEditMode
      ? String(this.detailForm.value.secret || '')
      : String(this.activeItemSecret || '');
    if (!this.detailEditMode && secretValue.startsWith('Falha ao descriptografar')) {
      return 0;
    }
    return this.getPasswordStrengthScore(secretValue);
  }

  private getPasswordStrengthScore(value?: string | null) {
    const secret = String(value || '');
    if (!secret) {
      return 0;
    }
    let score = 0;
    if (secret.length >= 8) {
      score += 1;
    }
    if (secret.length >= 12) {
      score += 1;
    }
    if (/[a-z]/.test(secret) && /[A-Z]/.test(secret)) {
      score += 1;
    }
    if (/\d/.test(secret)) {
      score += 1;
    }
    if (/[^A-Za-z0-9]/.test(secret)) {
      score += 1;
    }
    return score;
  }

  private isPasswordRelevantItem(item: ControlSafeItem) {
    return ['LOGIN', 'WIFI', 'CREDIT_CARD', 'API_KEY', 'BANK_ACCOUNT'].includes(String(item?.item_type || '').toUpperCase());
  }

  requiresAttachmentsForType(type: ItemType | null | undefined) {
    return ['DOCUMENT', 'API_KEY', 'BANK_ACCOUNT'].includes(String(type || '').toUpperCase());
  }

  private mergePasswordScores(items: ControlSafeItem[]) {
    const scoreMap = new Map<number, number>();
    for (const item of items || []) {
      const id = Number(item?.id || 0);
      const score = Number(item?.password_score || 0);
      if (id) {
        scoreMap.set(id, score);
      }
    }
    if (this.activeItem?.id) {
      const activeScore = this.detailPasswordStrengthScore;
      scoreMap.set(Number(this.activeItem.id), activeScore);
    }
    return (items || []).map((item) => {
      const id = Number(item?.id || 0);
      if (!id || !scoreMap.has(id)) {
        return item;
      }
      return {
        ...item,
        password_score: scoreMap.get(id) || item.password_score
      };
    });
  }

  get itemHistoryTotalPages() {
    return Math.max(1, Math.ceil(this.itemHistoryTotal / this.itemHistoryPageSize));
  }

  get itemHistoryPageNumbers() {
    return Array.from({ length: this.itemHistoryTotalPages }, (_, index) => index + 1);
  }
}
